// Alertas de completitud EN VIVO — panel de ADMIN (2026-09-10, ver
// packages/web/src/api/lib/calidad-hc-vivo.ts / rpc
// admin.alertasCompletitudVivas). A diferencia de AlertasCompletitudPropias.tsx
// (dashboard del médico: SUS PROPIOS Encounters, evaluados en el navegador) y
// de IndiceCompletitudHc (./IndiceCompletitudHc.tsx, retrospectivo, por
// muestreo al azar), esta es la vista agregada de TODOS los médicos del
// consultorio sobre los últimos 30 días, con nombre real de paciente y del
// último profesional que atendió cada Encounter — mismo guard de sesión que
// AuditoriaPage.tsx/ComitePage.tsx (leerSesionHceAdmin, nunca la de Medplum).
//
// Polling manual con `setInterval` (sin react-query, no está instalado en
// este paquete) — mismo criterio simple que el resto del panel de admin.
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { leerSesionHceAdmin } from '@/auth/hceAdminSession';
import { obtenerAlertasCompletitudVivas, type AlertaViva } from '@/lib/calidadHc';
import { alertasACsv, descargarCsv, nombreArchivoCsvAlertas } from '@/lib/calidadHcCsv';
import { TITULO_POR_ITEM } from '@/lib/pasosCalidadHc';
import { IndiceCompletitudHc } from './IndiceCompletitudHc';

/** Ventana pedida por el usuario: todos los médicos, últimos 30 días. */
const DIAS_VENTANA = 30;

/** Pedido explícito del brief: refresco automático cada 25s, sin depender de que el admin recargue la página a mano. */
const INTERVALO_POLLING_MS = 25_000;

export function AlertasVivasPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sesion = leerSesionHceAdmin();

  const [alertas, setAlertas] = useState<AlertaViva[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(
    async (token: string, slug: string) => {
      try {
        const resultado = await obtenerAlertasCompletitudVivas(token, slug, DIAS_VENTANA);
        setAlertas(resultado);
        setError(null);
      } catch {
        setError(t('admin.alertasVivas.error'));
      } finally {
        setCargando(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!sesion) return;
    void cargar(sesion.token, sesion.subdominio);
    // Polling manual — el widget se mantiene al día sin que el admin tenga
    // que recargar la pestaña mientras la deja abierta en un segundo monitor.
    const intervalId = setInterval(() => {
      void cargar(sesion.token, sesion.subdominio);
    }, INTERVALO_POLLING_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sesion) {
    return (
      <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4">
        <Alert variant="red" className="max-w-sm">
          <AlertDescription>{t('admin.usuarios.sesionVencida')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const lista = alertas ?? [];

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-hce-ink">{t('admin.alertasVivas.titulo')}</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void navigate('/auditoria')}>
              {t('admin.auditoria.titulo')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void navigate('/usuarios')}>
              {t('admin.alertasVivas.volver')}
            </Button>
          </div>
        </div>

        <IndiceCompletitudHc sesion={sesion} />

        <p className="text-xs text-hce-muted">{t('admin.alertasVivas.descripcion')}</p>

        {error && (
          <Alert variant="red">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {cargando && alertas === null ? (
          <p className="text-sm text-hce-muted">{t('admin.alertasVivas.cargando')}</p>
        ) : lista.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('admin.alertasVivas.sinAlertas')}</p>
        ) : (
          <>
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => descargarCsv(nombreArchivoCsvAlertas(sesion.subdominio), alertasACsv(lista))}
              >
                {t('admin.alertasVivas.exportarCsv')}
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.alertasVivas.columnas.paciente')}</TableHead>
                  <TableHead>{t('admin.alertasVivas.columnas.queFalta')}</TableHead>
                  <TableHead>{t('admin.alertasVivas.columnas.profesional')}</TableHead>
                  <TableHead>{t('admin.alertasVivas.columnas.fecha')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((a, i) => (
                  <TableRow key={`${a.encounterRef}-${a.clave}-${i}`}>
                    <TableCell>{a.patientName}</TableCell>
                    <TableCell>
                      <Badge variant="clay">{TITULO_POR_ITEM[a.clave]}</Badge>
                    </TableCell>
                    <TableCell>{a.practitionerName ?? '—'}</TableCell>
                    <TableCell className="text-hce-muted text-xs">{a.fecha ? new Date(a.fecha).toLocaleString('es-EC') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}
